/* =============================================================
   SW PDP  ·  Everyday Chemist
   Behaviour for the "sw-*" product-page sections.

   Everything here is progressive: with JS off the page still
   renders the first variant, the first tab and every gallery
   image, and the add-to-cart form still posts to /cart/add.

   Cross-section sync happens on one document-level event:
     'sw:variant'  detail = { id, index, title }
   The hero owns the selection; the scent explorer follows, and
   either one can originate a change.
   ============================================================= */
(function () {
  'use strict';

  /* ---------- reveal on scroll ------------------------------- */

  function initReveal(root) {
    var els = (root || document).querySelectorAll('.sw-reveal:not([data-sw-revealed])');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.06 });

    els.forEach(function (el) {
      el.setAttribute('data-sw-revealed', '');
      // Only arm elements that start below the fold — anything already
      // on screen stays visible so nothing flashes in on load.
      if (el.getBoundingClientRect().top > window.innerHeight * 0.9) {
        el.classList.add('is-armed');
        io.observe(el);
      }
    });
  }

  /* ---------- money ------------------------------------------ */

  function formatMoney(cents) {
    var fmt = (window.KROWN && KROWN.settings && KROWN.settings.shop_money_format) || '${{amount}}';
    var amount = cents / 100;

    function withDelimiters(n, precision, thousands, decimal) {
      var parts = n.toFixed(precision).split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? intPart + decimal + parts[1] : intPart;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_m, name) {
      switch (name) {
        case 'amount': return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_space_separator': return withDelimiters(amount, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(amount, 0, ' ', ',');
        default: return withDelimiters(amount, 2, ',', '.');
      }
    });
  }

  /* ---------- hero ------------------------------------------- */

  function initHero(root) {
    var hero = root.matches && root.matches('[data-sw-hero]') ? root : root.querySelector('[data-sw-hero]');
    if (!hero || hero.__swInit) return;
    hero.__swInit = true;

    var dataEl = hero.querySelector('[data-sw-hero-data]');
    var data = {};
    try { data = JSON.parse(dataEl.textContent); } catch (e) { data = { variants: [] }; }
    var variants = data.variants || [];

    var idInput   = hero.querySelector('[data-sw-variant-id]');
    var priceEl   = hero.querySelector('[data-sw-price]');
    var addBtn    = hero.querySelector('[data-sw-add]');
    var addLabel  = hero.querySelector('[data-sw-add-label]');
    var optionOut = hero.querySelector('[data-sw-option-name]');
    var qtyInput  = hero.querySelector('[data-sw-qty]');
    var stickyScent = document.querySelector('[data-sw-sticky-scent]');

    var optBtns = hero.querySelectorAll('[data-sw-opt]');
    var optNames = hero.querySelectorAll('[data-sw-optname]');

    // Index of the variant whose options equal `opts` exactly, or -1.
    function findByOptions(opts) {
      for (var i = 0; i < variants.length; i++) {
        var o = variants[i].options || [];
        var same = o.length === opts.length;
        for (var j = 0; same && j < o.length; j++) if (o[j] !== opts[j]) same = false;
        if (same) return i;
      }
      return -1;
    }

    // Change one option and keep the others; if that exact combination does not exist,
    // fall back to the first (preferably available) variant carrying the value.
    function indexForOption(position, value) {
      var cur = variants[current] || variants[0];
      var opts = (cur.options || []).slice();
      opts[position] = value;
      var idx = findByOptions(opts);
      if (idx > -1) return idx;
      for (var pass = 0; pass < 2; pass++) {
        for (var i = 0; i < variants.length; i++) {
          if ((variants[i].options || [])[position] === value && (pass === 1 || variants[i].available)) return i;
        }
      }
      return -1;
    }

    function announce(index) {
      var v = variants[index];
      if (!v) return;
      document.dispatchEvent(new CustomEvent('sw:variant', {
        detail: { id: v.id, index: index, title: v.title, options: v.options || [], source: 'hero' }
      }));
    }

    var current = 0;
    variants.some(function (v, i) {
      if (idInput && String(v.id) === String(idInput.value)) { current = i; return true; }
      return false;
    });

    function lineTotal(v) {
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      return formatMoney(v.price * qty);
    }

    function paint(index, push) {
      var v = variants[index];
      if (!v) return;
      current = index;

      if (idInput) idInput.value = v.id;
      if (priceEl) {
        priceEl.innerHTML = formatMoney(v.price) +
          (v.compare_at_price && v.compare_at_price > v.price ? ' <s>' + formatMoney(v.compare_at_price) + '</s>' : '');
      }
      if (optionOut) optionOut.textContent = v.title;
      if (stickyScent) stickyScent.textContent = v.title;

      hero.querySelectorAll('[data-sw-scent]').forEach(function (btn) {
        var on = parseInt(btn.getAttribute('data-sw-scent'), 10) === index;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      optBtns.forEach(function (btn) {
        var pos = parseInt(btn.getAttribute('data-sw-opt'), 10);
        var value = btn.getAttribute('data-value');
        var opts = (v.options || []).slice();
        btn.setAttribute('aria-pressed', opts[pos] === value ? 'true' : 'false');
        opts[pos] = value;
        var m = variants[findByOptions(opts)];
        btn.classList.toggle('is-unavailable', !m || !m.available);
      });
      optNames.forEach(function (el) {
        el.textContent = (v.options || [])[parseInt(el.getAttribute('data-sw-optname'), 10)] || '';
      });

      // Show the variant's own image in the gallery when it has one.
      if (v.media_index !== null && v.media_index !== undefined) selectSlide(v.media_index);

      if (addBtn) {
        addBtn.disabled = !v.available;
        addBtn.classList.toggle('is-disabled', !v.available);
      }
      if (addLabel) {
        addLabel.textContent = v.available
          ? (data.add_label || 'Add to bag') + ' — ' + lineTotal(v)
          : (data.sold_out_label || 'Sold out');
      }

      if (push && window.history && history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url.toString());
      }
    }

    /* gallery */
    var slides = hero.querySelectorAll('[data-sw-slide]');
    var thumbs = hero.querySelectorAll('[data-sw-thumb]');

    function selectSlide(i) {
      if (!slides.length) return;
      if (i < 0 || i >= slides.length) return;
      slides.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
      thumbs.forEach(function (t, n) { t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { selectSlide(i); });
    });

    /* scent buttons */
    hero.querySelectorAll('[data-sw-scent]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.getAttribute('data-sw-scent'), 10);
        paint(i, true);
        announce(i);
      });
    });

    /* option rows (products with more than one option) */
    optBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = indexForOption(parseInt(btn.getAttribute('data-sw-opt'), 10), btn.getAttribute('data-value'));
        if (idx < 0) return;
        paint(idx, true);
        announce(idx);
      });
    });

    /* quantity */
    if (qtyInput) {
      var clamp = function () {
        var n = parseInt(qtyInput.value, 10);
        if (isNaN(n) || n < 1) n = 1;
        if (n > 99) n = 99;
        qtyInput.value = n;
        paint(current, false);
      };
      hero.querySelectorAll('[data-sw-qty-step]').forEach(function (b) {
        b.addEventListener('click', function () {
          var step = parseInt(b.getAttribute('data-sw-qty-step'), 10) || 1;
          qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + step;
          clamp();
        });
      });
      qtyInput.addEventListener('change', clamp);
    }

    /* follow selections made elsewhere (scent explorer) */
    document.addEventListener('sw:variant', function (e) {
      if (!e.detail || e.detail.source === 'hero' || typeof e.detail.index !== 'number') return;
      paint(e.detail.index, true);
    });

    // An explorer picked an option value: position is fixed, or found automatically.
    document.addEventListener('sw:option', function (e) {
      var d = e.detail;
      if (!d || d.value == null) return;
      var position = typeof d.position === 'number' && d.position >= 0 ? d.position : -1;
      if (position < 0) {
        var width = ((variants[current] || variants[0] || {}).options || []).length;
        for (var p = 0; p < width && position < 0; p++) {
          for (var i = 0; i < variants.length; i++) {
            if ((variants[i].options || [])[p] === d.value) { position = p; break; }
          }
        }
      }
      if (position < 0) return;
      var idx = indexForOption(position, d.value);
      if (idx < 0 || idx === current) return;
      paint(idx, true);
      announce(idx);
    });

    paint(current, false);

    /* sticky mobile bar — mirrors the hero's form via requestSubmit */
    var bar = document.querySelector('[data-sw-stickybar]');
    if (bar) {
      var form = hero.querySelector('form[action*="/cart/add"]');
      var barBtn = bar.querySelector('[data-sw-sticky-add]');
      if (form && barBtn) {
        barBtn.addEventListener('click', function () {
          var submitter = form.querySelector('[data-sw-add]');
          if (form.requestSubmit) form.requestSubmit(submitter);
          else form.submit();
        });
      }
      // Show the bar once the real buy box has scrolled past. A scroll listener
      // rather than an IntersectionObserver: observers are paused in background
      // tabs, which can leave the bar stuck in the wrong state on return.
      var buyBox = hero.querySelector('[data-sw-buybox]');
      if (buyBox) {
        var sync = function () {
          bar.classList.toggle('is-visible', buyBox.getBoundingClientRect().bottom < 0);
        };
        window.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync, { passive: true });
        sync();
      }
    }
  }

  /* ---------- scent explorer --------------------------------- */

  function initScents(root) {
    (root.querySelectorAll ? root.querySelectorAll('[data-sw-scents]') : []).forEach(function (sec) {
      if (sec.__swInit) return;
      sec.__swInit = true;

      var panels = sec.querySelectorAll('[data-sw-scent-panel]');
      var pills  = sec.querySelectorAll('[data-sw-scent-pill]');
      var nameOut = sec.querySelector('[data-sw-scent-name]');
      // -1 automatic, -2 no sync, 0..2 a fixed option position.
      var position = parseInt(sec.getAttribute('data-sw-option-position'), 10);
      if (isNaN(position)) position = -1;

      function show(key) {
        var active = null;
        panels.forEach(function (p) {
          if (p.getAttribute('data-sw-scent-panel') === key) active = p;
        });
        // A value this explorer has no panel for leaves the current panel alone.
        if (!active) return false;
        panels.forEach(function (p) { p.classList.toggle('is-active', p === active); });
        pills.forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-sw-scent-pill') === key ? 'true' : 'false');
        });
        if (nameOut) nameOut.textContent = active.getAttribute('data-sw-scent-title') || '';
        return true;
      }

      pills.forEach(function (b) {
        b.addEventListener('click', function () {
          var key = b.getAttribute('data-sw-scent-pill');
          show(key);
          if (b.getAttribute('data-sync') !== 'true') return;
          document.dispatchEvent(new CustomEvent('sw:option', {
            detail: { value: key, position: position, source: 'scents' }
          }));
        });
      });

      document.addEventListener('sw:variant', function (e) {
        var d = e.detail;
        if (!d || d.source === 'scents' || position === -2) return;
        var opts = d.options || [];
        var candidates = position >= 0 ? [opts[position]] : [d.title].concat(opts);
        for (var i = 0; i < candidates.length; i++) {
          if (candidates[i] != null && show(candidates[i])) break;
        }
      });
    });
  }

  /* ---------- shop grid: instant category filter ---------------
     Tabs are real links to real collections, so the page works with
     JS off and the links stay crawlable. With JS we intercept the
     click and filter what is already rendered, which is instant. */

  function initGrid(root) {
    (root.querySelectorAll ? root.querySelectorAll('[data-sw-grid]') : []).forEach(function (sec) {
      if (sec.__swInit) return;
      sec.__swInit = true;

      var tabs  = sec.querySelectorAll('[data-sw-grid-tab]');
      var cards = sec.querySelectorAll('[data-sw-cats]');
      var count = sec.querySelector('[data-sw-grid-count]');
      var countWrap = sec.querySelector('[data-sw-grid-countwrap]');
      var products = sec.querySelector('[data-sw-grid-products]');
      var empty = sec.querySelector('[data-sw-grid-empty]');
      if (!tabs.length) return;

      var one = sec.getAttribute('data-sw-label-one') || 'product';
      var many = sec.getAttribute('data-sw-label-many') || 'products';

      // "Section tabs" (key "panel:<id>") swap the products for another section on the page.
      var panels = {};
      tabs.forEach(function (t) {
        var key = t.getAttribute('data-sw-grid-tab');
        if (key.indexOf('panel:') !== 0) return;
        var el = document.getElementById(key.slice(6));
        if (el) panels[key] = el;
      });

      function apply(key, fromUrl) {
        var isPanel = !!panels[key];
        var shown = 0;
        if (!isPanel) {
          cards.forEach(function (card) {
            var cats = (card.getAttribute('data-sw-cats') || '').split(' ');
            var on = key === 'all' || cats.indexOf(key) > -1;
            card.hidden = !on;
            if (on) shown++;
          });
          if (count) count.textContent = shown + ' ' + (shown === 1 ? one : many);
          if (empty) empty.hidden = shown > 0;
        }
        if (products) products.hidden = isPanel;
        if (countWrap) countWrap.hidden = isPanel;
        Object.keys(panels).forEach(function (k) { panels[k].hidden = k !== key; });
        sec.classList.toggle('is-panel', isPanel);

        tabs.forEach(function (t) {
          t.setAttribute('aria-current', t.getAttribute('data-sw-grid-tab') === key ? 'true' : 'false');
        });

        if (!fromUrl && window.history && history.replaceState) {
          var base = location.pathname + location.search;
          history.replaceState(null, '', isPanel ? base + '#' + key.slice(6) : base);
        }
        if (isPanel) document.dispatchEvent(new CustomEvent('sw:panel', { detail: { id: key.slice(6) } }));
      }

      tabs.forEach(function (t) {
        var key = t.getAttribute('data-sw-grid-tab');
        if (key.indexOf('panel:') === 0 && !panels[key]) return; // no such section: leave the link alone
        t.addEventListener('click', function (e) {
          // Let modified clicks (new tab, download) behave normally.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          apply(key);
        });
      });

      // Deep link: /collections/all#build-a-bundle opens that tab and brings the tabs into view,
      // on load and when a link on this same page only changes the hash.
      var bar = sec.querySelector('.sw-grid__bar');
      function fromHash() {
        var hashKey = 'panel:' + decodeURIComponent((location.hash || '').slice(1));
        if (!panels[hashKey]) return;
        apply(hashKey, true);
        if (bar) {
          var headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ec-header-height'), 10) || 0;
          window.scrollTo({ top: bar.getBoundingClientRect().top + window.scrollY - headerH - 24, behavior: 'auto' });
        }
      }
      fromHash();
      window.addEventListener('hashchange', fromHash);
      // Images and fonts above the tabs can shift the layout after the first scroll; settle it once loaded.
      if (document.readyState !== 'complete') window.addEventListener('load', fromHash, { once: true });

      // Theme editor: selecting a panel section reveals it.
      document.addEventListener('shopify:section:select', function (e) {
        Object.keys(panels).forEach(function (k) {
          if (e.target.contains(panels[k])) apply(k, true);
        });
      });
    });
  }

  /* ---------- tabs ------------------------------------------- */

  function initTabs(root) {
    (root.querySelectorAll ? root.querySelectorAll('[data-sw-tabs]') : []).forEach(function (sec) {
      if (sec.__swInit) return;
      sec.__swInit = true;

      var tabs = Array.prototype.slice.call(sec.querySelectorAll('[data-sw-tab]'));
      var panels = sec.querySelectorAll('[data-sw-panel]');

      function show(id) {
        tabs.forEach(function (t) { t.setAttribute('aria-selected', t.getAttribute('data-sw-tab') === id ? 'true' : 'false'); });
        panels.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-sw-panel') === id); });
      }

      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { show(t.getAttribute('data-sw-tab')); });
        t.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (!next) return;
          e.preventDefault();
          next.focus();
          show(next.getAttribute('data-sw-tab'));
        });
      });
    });
  }

  /* ---------- marquee: duplicate the run so the loop is seamless */

  function initMarquee(root) {
    (root.querySelectorAll ? root.querySelectorAll('[data-sw-marquee]') : []).forEach(function (track) {
      if (track.__swInit) return;
      track.__swInit = true;
      track.insertAdjacentHTML('beforeend', track.innerHTML);
    });
  }

  /* ---------- boot ------------------------------------------- */

  function boot(root) {
    root = root || document;
    initHero(root);
    initScents(root);
    initTabs(root);
    initGrid(root);
    initMarquee(root);
    initReveal(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Theme editor: re-init a section after it is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });

  window.SWPDP = { boot: boot, formatMoney: formatMoney };
})();
